import { Box, Group, Paper, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';

export type CalloutTone = 'ok' | 'warn' | 'bad';

const TONES: Record<
  CalloutTone,
  { bg: string; border: string; dot: string; strong: boolean }
> = {
  ok: {
    bg: 'medleyGreen.0',
    border: 'medleyGreen.2',
    dot: 'medleyGreen.6',
    strong: false,
  },
  warn: {
    bg: 'warningAmber.1',
    border: 'warningAmber.5',
    dot: 'warningAmber.5',
    strong: true,
  },
  bad: {
    bg: 'dangerRed.0',
    border: 'dangerRed.5',
    dot: 'dangerRed.5',
    strong: true,
  },
};

/** A colored status line with a dot: feasibility, capacity, checks. */
export function Callout({
  tone,
  children,
}: {
  tone: CalloutTone;
  children: ReactNode;
}) {
  const t = TONES[tone];
  return (
    <Paper
      bg={t.bg}
      p="sm"
      radius="md"
      style={{
        border: `1px solid var(--mantine-color-${t.border.replace('.', '-')})`,
        borderLeftWidth: tone === 'ok' ? 1 : 6,
      }}
    >
      <Group gap={10} wrap="nowrap" align="flex-start">
        <Box
          w={10}
          h={10}
          mt={5}
          bg={t.dot}
          style={{ borderRadius: 5, flex: 'none' }}
        />
        <Text
          size="sm"
          fw={t.strong ? 600 : 400}
          c={tone === 'bad' ? 'dangerRed.9' : undefined}
        >
          {children}
        </Text>
      </Group>
    </Paper>
  );
}

interface WhatThisMeansProps {
  lines: string[];
  callout?: { tone: CalloutTone; text: string };
  /** Put the callout beside the lines instead of below them. */
  sideBySide?: boolean;
}

/** The gray "What this means" panel used across the setup flow. */
export function WhatThisMeans({
  lines,
  callout,
  sideBySide,
}: WhatThisMeansProps) {
  const body = (
    <Stack gap={8} style={{ flex: '1 1 380px', minWidth: 0 }}>
      <Text fw={600}>What this means</Text>
      {lines.map(l => (
        <Text key={l} size="sm" lh={1.5}>
          {l}
        </Text>
      ))}
    </Stack>
  );
  return (
    <Paper bg="gray.0" withBorder p="lg" radius="md">
      {sideBySide ? (
        <Group align="flex-start" gap="xl" wrap="wrap">
          {body}
          {callout && (
            <Box style={{ flex: '0 1 340px' }}>
              <Callout tone={callout.tone}>{callout.text}</Callout>
            </Box>
          )}
        </Group>
      ) : (
        <Stack gap="md">
          {body}
          {callout && <Callout tone={callout.tone}>{callout.text}</Callout>}
        </Stack>
      )}
    </Paper>
  );
}
